import { CurrentCompanyType, ThemeType } from "@/interfaces";
import api from "@/services/api";
import { useUserStore } from "@/stores/userStore";
import { useEffect, useState } from "react";

const DEFAULT_THEME: ThemeType = {
  logo: null,
  favicon: null,
  primaryColor: "#1677ff",
  secondaryColor: "#0e172b",
  sidebarBackground: "#0e172b",
};

// PowerBiUser accounts are assigned a real company for dashboard-scoping purposes (see
// useGetUserDashboards call sites), but the app chrome should always show Vertoz's own branding
// for this role instead of that company's. This is the only "Vertoz"-named company in the system
// with actual logo/colors configured (VERTOZ LIMITED) -- there is no row literally named
// "Vertoz Ltd".
const VERTOZ_BRANDING_COMPANY_ID = "f5a1cbf6-1033-4f4c-85e1-a1298ef0f372";

export const useTheme = () => {
  const [theme, setTheme] = useState<ThemeType>(DEFAULT_THEME);
  const currentCompany = useUserStore((state) => state.currentCompany);
  const user = useUserStore((state) => state.user);
  const isPowerBiUser = user?.role === "PowerBiUser";
  // Plain axios fetch rather than the react-query-backed useGetCompanyById: this hook is called
  // from main.tsx *before* QueryClientProvider mounts (for antd's early color theming), so any
  // useQuery-based hook here throws "No QueryClient set" on every render, everywhere in the app.
  const [vertozBrandingCompany, setVertozBrandingCompany] = useState<CurrentCompanyType | null>(null);
  useEffect(() => {
    if (!isPowerBiUser) return;
    // getCompanyById has no membership/ownership check, so this resolves regardless of which
    // company the PowerBiUser is actually assigned to.
    api
      .get("/company/id", { params: { id: VERTOZ_BRANDING_COMPANY_ID } })
      .then((res) => setVertozBrandingCompany(res.data?.company ?? null))
      .catch(() => setVertozBrandingCompany(null));
  }, [isPowerBiUser]);

  useEffect(() => {
    // Finance users intentionally have no single `currentCompany` — they operate across all their
    // assigned companies, so App.tsx nulls it out on load. Their branding still comes from their
    // company, so fall back to the first company on the persisted user object (populated from
    // /current, which carries logo/colors/gradient) when currentCompany is unset. For roles that do
    // have a currentCompany this fallback never triggers; for Super (no companies) it stays default.
    // PowerBiUser is the one exception: its branding source is always Vertoz's own company record,
    // never currentCompany (which only drives dashboard scoping for this role).
    const company = isPowerBiUser
      ? vertozBrandingCompany
      : currentCompany || ((user?.companies?.[0] as unknown as CurrentCompanyType | undefined) ?? null);

    if (company) {
      const secondaryColor = company.secondaryColor || DEFAULT_THEME.secondaryColor;
      const sidebarBackground =
        company.sidebarGradientEnabled && company.sidebarGradientStart && company.sidebarGradientEnd
          ? `linear-gradient(180deg, ${company.sidebarGradientStart}, ${company.sidebarGradientEnd})`
          : secondaryColor;

      setTheme({
        logo: company.logoResource?.presignedUrl || null,
        favicon: company.faviconResource?.presignedUrl || null,
        primaryColor: company.primaryColor || DEFAULT_THEME.primaryColor,
        secondaryColor,
        sidebarBackground,
      });
    } else {
      setTheme(DEFAULT_THEME);
    }
  }, [currentCompany, user, isPowerBiUser, vertozBrandingCompany]);

  return {
    theme,
  };
};
